// Guest details — the screen shaped entirely by how hotel suppliers model
// travellers. Read this before changing the form.
//
// A hotel is priced per room, and a room's *rate* carries its own head count.
// The supplier therefore accepts exactly **one guest entry per room** and
// rejects a second on the same occupancy outright. So this page renders one card
// per room, not one per person:
//
//   Room 1 (2 adults, 1 child)
//     └ Lead guest        → firstName / lastName, and contact on the lead room
//     └ Also in this room → the other adult and the child, by name
//
// The "also in this room" names are not decoration and not dropped: they travel
// to the property as that room's free-text remarks, which is the only channel
// the schema leaves for them. That is why they are collected at all, and why the
// count is pre-seeded from the occupancy the room was priced at — a partner
// shouldn't have to remember how many people they searched for.
//
// Hotels need **no travel documents** — no passport, no per-guest nationality.
// The itinerary says so on `guest_rules.documents_required`; don't collect what
// the supplier will reject.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { useBodyClass } from "../lib/useBodyClass";
import { occupancyLabel } from "../lib/hotelFormat";
import { HotelSummaryCard } from "../components/HotelSummaryCard";
import type { HotelGuest, HotelItinerary, Occupancy, Occupant } from "../lib/hotelTypes";

/** Seed the occupants of a room from the occupancy it was priced at.
 *
 *  Room 1 of `{adults: 2, children: [8]}` needs one lead guest plus two blank
 *  occupants — the second adult and the 8-year-old, whose age is already known
 *  because the rate was priced on it. */
function seedGuest(occupancy: Occupancy, index: number): HotelGuest {
  const others: Occupant[] = [];
  for (let i = 1; i < (occupancy.adults || 1); i++) {
    others.push({ first_name: "", last_name: "", pax_type: "adult" });
  }
  for (const age of occupancy.children || []) {
    others.push({ first_name: "", last_name: "", pax_type: "child", age });
  }
  return {
    occupancy_number: index + 1,
    title: "Mr",
    first_name: "",
    last_name: "",
    is_lead: index === 0,
    email: "",
    contact_number: "",
    cell_country_code: "91",
    special_requests: "",
    other_occupants: others,
  };
}

export function HotelGuests() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const bookingQ = useQuery({
    queryKey: ["hotelBooking", bookingId],
    queryFn: () => api.get<HotelItinerary>(`/v1/hotels/booking/${bookingId}/`),
    enabled: !!bookingId,
  });

  const [guests, setGuests] = useState<HotelGuest[] | null>(null);

  useEffect(() => {
    if (guests || !bookingQ.data) return;
    const occupancies = bookingQ.data.occupancies || [];
    setGuests(occupancies.map(seedGuest));
  }, [bookingQ.data, guests]);

  const save = useMutation({
    mutationFn: (body: { guests: HotelGuest[] }) =>
      api.post<HotelItinerary>(`/v1/hotels/booking/${bookingId}/guests/`, body),
    onSuccess: (data) => {
      if (bookingId) qc.setQueryData(["hotelBooking", bookingId], data);
      nav(`/booking/${bookingId}/payment`);
    },
  });

  function updateGuest(index: number, patch: Partial<HotelGuest>) {
    setGuests((cur) => cur && cur.map((g, i) => (i === index ? { ...g, ...patch } : g)));
  }

  function updateOccupant(guestIndex: number, occupantIndex: number, patch: Partial<Occupant>) {
    setGuests((cur) => cur && cur.map((g, i) => {
      if (i !== guestIndex) return g;
      const others = [...(g.other_occupants || [])];
      others[occupantIndex] = { ...others[occupantIndex], ...patch };
      return { ...g, other_occupants: others };
    }));
  }

  function onSubmit() {
    if (!guests) return;
    const payload = guests.map((g) => {
      const clean: HotelGuest = { ...g };
      // Only the lead room carries contact details; the supplier's holder block
      // is built from it, and an empty string on the others is not the same as
      // an absent field.
      if (!g.is_lead) {
        delete clean.email;
        delete clean.contact_number;
        delete clean.cell_country_code;
      }
      if (!clean.special_requests) delete clean.special_requests;
      // Drop unnamed occupants rather than sending blanks — a half-filled name
      // reaches the front desk as a half-filled name.
      clean.other_occupants = (g.other_occupants || []).filter((o) => o.first_name.trim());
      if (!clean.other_occupants.length) delete clean.other_occupants;
      return clean;
    });
    save.mutate({ guests: payload });
  }

  const formInvalid = useMemo(() => {
    if (!guests) return true;
    return guests.some((g) => {
      if (!g.first_name.trim() || !g.last_name.trim()) return true;
      if (g.is_lead && (!g.email?.trim() || !g.contact_number?.trim())) return true;
      return false;
    });
  }, [guests]);

  if (bookingQ.isLoading) return <GuestsSkeleton />;
  if (bookingQ.error) return <div className="app-error">Could not load booking.</div>;
  if (!bookingQ.data || !guests) return null;

  const booking = bookingQ.data;
  const rules = booking.guest_rules;
  const errMsg = save.error instanceof ApiError ? save.error.message : null;

  return (
    <>
      <HotelFlowStepper current="guests" />

      <div className="container">
        <div className="pax-wrap">
          <div>
            <div className="pax-header">
              <h1 className="pax-h">Who's <span className="serif">staying</span>?</h1>
              <p className="pax-sub">
                <b>{booking.hotel.name}</b> · {occupancyLabel(booking.occupancies)}.{" "}
                {/* Stated up front, because "why only one name per room?" is the
                    single most predictable question on this screen. */}
                Hotels take <b>one name per room</b> — add everyone else as an
                occupant so the property knows to expect them.
                {/* Some rates are priced on every occupant being named and are
                    rejected outright without them, so the ask changes from a
                    courtesy to a requirement. */}
                {rules?.all_occupants_required
                  ? " This rate needs a name for everyone staying."
                  : ""}
                {rules?.documents_required === false && " No passport details needed."}
              </p>
            </div>

            {guests.map((guest, index) => (
              <RoomGuestCard
                key={index}
                index={index}
                guest={guest}
                occupancy={booking.occupancies[index]}
                occupantsRequired={!!rules?.all_occupants_required}
                onChange={(patch) => updateGuest(index, patch)}
                onOccupantChange={(oi, patch) => updateOccupant(index, oi, patch)}
              />
            ))}

            {errMsg && <div className="app-error" style={{ margin: "12px 0" }}>{errMsg}</div>}
          </div>

          <HotelSummaryCard
            booking={booking}
            ctaLabel="Save & continue"
            onCta={onSubmit}
            ctaDisabled={formInvalid}
            ctaLoading={save.isPending}
            helpText="Next we'll re-confirm the rate with the property, then debit your wallet."
          />
        </div>
      </div>
    </>
  );
}

// ─── one room ───────────────────────────────────────────────────────────────

function RoomGuestCard(props: {
  index: number;
  guest: HotelGuest;
  occupancy?: Occupancy;
  /** True when the supplier rejects the booking unless every occupant is named. */
  occupantsRequired?: boolean;
  onChange: (patch: Partial<HotelGuest>) => void;
  onOccupantChange: (occupantIndex: number, patch: Partial<Occupant>) => void;
}) {
  const { guest, occupancy } = props;
  const others = guest.other_occupants || [];
  const occupancySummary = occupancy
    ? [
        `${occupancy.adults} adult${occupancy.adults === 1 ? "" : "s"}`,
        occupancy.children?.length
          ? `${occupancy.children.length} child${occupancy.children.length === 1 ? "" : "ren"}`
          : null,
      ].filter(Boolean).join(" · ")
    : "";

  return (
    <>
      <section className="form-card">
        <div className="form-card-head">
          <h3>
            Room {props.index + 1}
            {guest.is_lead && <span className="lead-badge">Lead guest</span>}
          </h3>
          <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>{occupancySummary}</span>
        </div>

        <div className="form-row title-name">
          <Field label="Title" required>
            <select value={guest.title} onChange={(e) => props.onChange({ title: e.target.value })}>
              {["Mr", "Ms", "Mrs", "Dr"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="First name" required>
            <input
              value={guest.first_name}
              onChange={(e) => props.onChange({ first_name: e.target.value })}
              placeholder="As on ID"
            />
          </Field>
          <Field label="Last name" required>
            <input
              value={guest.last_name}
              onChange={(e) => props.onChange({ last_name: e.target.value })}
            />
          </Field>
        </div>

        {guest.is_lead && (
          <div className="form-row cols-2">
            <Field label="Email" required>
              <input
                type="email"
                value={guest.email || ""}
                onChange={(e) => props.onChange({ email: e.target.value })}
              />
            </Field>
            <Field label="Mobile" required>
              <input
                type="tel"
                value={guest.contact_number || ""}
                onChange={(e) => props.onChange({ contact_number: e.target.value })}
                placeholder="9876543210"
              />
            </Field>
          </div>
        )}

        {!!others.length && (
          <>
            <div className="occupants-head">
              Also staying in this room
              <span>
                {/* Says where the names actually go, since it isn't obvious that
                    a hotel booking has no field for them — and whether leaving
                    them blank is allowed, which depends on the rate. */}
                {props.occupantsRequired
                  ? "Required by this rate — the booking is refused without them"
                  : "Sent to the property with your booking"}
              </span>
            </div>
            {others.map((occupant, oi) => (
              <div className="form-row cols-3" key={oi}>
                <Field label={occupant.pax_type === "child"
                  ? `Child${occupant.age != null ? ` · age ${occupant.age}` : ""}`
                  : `Adult ${oi + 2}`}>
                  <input
                    value={occupant.first_name}
                    onChange={(e) => props.onOccupantChange(oi, { first_name: e.target.value })}
                    placeholder="First name"
                  />
                </Field>
                <Field label="Last name" optional>
                  <input
                    value={occupant.last_name || ""}
                    onChange={(e) => props.onOccupantChange(oi, { last_name: e.target.value })}
                  />
                </Field>
                <div className="field">
                  <label>&nbsp;</label>
                  <div className="occupant-note">
                    {occupant.pax_type === "child"
                      ? "Priced at this age — it can't be changed here."
                      : "Shares the room rate."}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        <div className="form-row">
          <Field label="Special requests" optional>
            <input
              value={guest.special_requests || ""}
              onChange={(e) => props.onChange({ special_requests: e.target.value })}
              placeholder="High floor, late arrival, connecting rooms…"
              maxLength={240}
            />
          </Field>
        </div>
        <p className="field-help">
          Requests reach the property but are never guaranteed — the hotel decides
          on arrival.
        </p>
      </section>
    </>
  );
}

function Field({ label, required, optional, children }: {
  label: string; required?: boolean; optional?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label>
        {label}{" "}
        {required && <span className="req">●</span>}
        {optional && <span className="opt">· optional</span>}
      </label>
      {children}
    </div>
  );
}

// ─── flow stepper ───────────────────────────────────────────────────────────

export function HotelFlowStepper({ current }: { current: "guests" | "payment" }) {
  const steps = [
    { key: "guests", label: "Guest details" },
    { key: "payment", label: "Review & pay" },
  ];
  const currentIdx = steps.findIndex((s) => s.key === current);
  return (
    <div className="stepper-wrap">
      <div className="stepper">
        {steps.map((s, i) => (
          <Fragment key={s.key}>
            <div className={`stepper-step ${i < currentIdx ? "done" : i === currentIdx ? "active" : ""}`}>
              <div className="num"><span className="n-digit">{i + 1}</span></div>
              <div className="label">{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className={`stepper-line ${i < currentIdx ? "done" : ""}`} />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function GuestsSkeleton() {
  return (
    <>
      <HotelFlowStepper current="guests" />
      <div className="container">
        <div className="pax-wrap">
          <div>
            <div className="pax-header">
              <span className="skeleton skeleton-line lg" style={{ width: 260 }} />
              <div style={{ marginTop: 10 }}>
                <span className="skeleton skeleton-line" style={{ width: 440 }} />
              </div>
            </div>
            <section className="form-card">
              <span className="skeleton skeleton-line" style={{ width: 160, marginBottom: 14 }} />
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 10 }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                ))}
              </div>
            </section>
          </div>
          <aside className="booking-sidebar">
            <div className="summary-card">
              <span className="skeleton skeleton-line lg" style={{ width: 160 }} />
              <div style={{ marginTop: 16 }}>
                <span className="skeleton" style={{ height: 90, width: "100%", borderRadius: 12 }} />
              </div>
              <div style={{ marginTop: 18 }}>
                <span className="skeleton" style={{ height: 44, width: "100%", borderRadius: 999 }} />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
